import {
  domainBlockCodes,
  domainBlockManifests,
  sourceContractVersion,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import type {
  WorkflowDefinitionDocument,
  WorkflowProcessDataField,
} from './platform-workflow-definition-lifecycle.models';
import type { DomainSource } from './workflow-domain-source.models';
import { processDataErrors } from './workflow-process-data';

export type NoticeSourceResolution =
  | {
      kind: 'resolved';
      source: DomainSource;
      label: string;
      origin: 'step' | 'workflow' | 'reference';
    }
  | {
      kind: 'choice';
      options: { key: string; label: string }[];
      issue?: string;
    }
  | { kind: 'missing' }
  | { kind: 'blocked'; message: string };

/** Resolve a source without replacing an established domain-group binding. */
export function resolveNoticeSource(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  selectedField?: string,
  pendingField?: WorkflowProcessDataField,
): NoticeSourceResolution {
  const nodes = document.nodes.filter((node) => node.id === nodeId);
  if (nodes.length > 1)
    return blocked(
      'This step has a duplicate identity. Resolve the duplicate workflow step before adding notice work.',
    );
  if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step')
    return blocked('Choose one work step before adding notice work.');
  const declaration = nodes[0].declaration;

  const domains = (declaration?.detail.blocks ?? []).filter((block) =>
    domainBlockCodes.has(block.code),
  );
  if (domains.length) {
    if (new Set(domains.map((block) => block.code)).size !== domains.length)
      return blocked(
        'This step has duplicate domain blocks. Keep one block of each kind in Staff page before adding notice work.',
      );
    for (const block of domains) {
      const manifest = domainBlockManifests.find(
        (item) =>
          item.code === block.code &&
          item.provider === block.provider &&
          item.version === block.version &&
          item.settingsSchema.version === block.settingsVersion,
      );
      if (!manifest)
        return blocked(
          'An existing domain block has an unavailable source contract. Review its installed version in Staff page before adding notice work.',
        );
      if (sourceContractVersion(block) !== 2)
        return blocked(
          'Review and upgrade the whole domain group in Staff page before adding notice work.',
        );
    }
    const settings = domains[0].settings;
    const mode = settings['bindingMode'];
    const reference = settings['referenceField'];
    if (
      !['linked-subject', 'process-reference'].includes(String(mode)) ||
      domains.some(
        (block) =>
          block.settings['bindingMode'] !== mode ||
          (mode === 'process-reference' &&
            block.settings['referenceField'] !== reference),
      )
    )
      return blocked(
        'This step has inconsistent domain sources. Choose one Hearing source for the whole domain group in Staff page before adding notice work.',
      );
    if (mode === 'linked-subject') {
      if (domains.some((block) => block.settings['referenceField'] != null))
        return blocked(
          'This step’s linked source also contains a shared-value reference. Review and choose one source for the whole domain group in Staff page before adding notice work.',
        );
      return document.subjectTypeCode === 'hearing_cycle'
        ? {
            kind: 'resolved',
            source: { bindingMode: 'linked-subject' },
            label: 'This workflow’s hearing',
            origin: 'step',
          }
        : blocked(
            'This step’s linked source is not a hearing. Choose a Hearing reference for the whole domain group in Staff page before adding notice work.',
          );
    }

    const matches = (document.processData?.fields ?? []).filter(
      (field) => field.key === reference,
    );
    if (
      matches.length === 1 &&
      matches[0].type === 'record-reference' &&
      matches[0].recordTypeCode === 'incarcerated-person'
    )
      return blocked(
        'This step uses an Inmate reference. Notices need a specific Hearing; choose a Hearing reference for the whole domain group in Staff page before adding notice work.',
      );
    if (
      typeof reference !== 'string' ||
      matches.length !== 1 ||
      !isHearingField(matches[0]) ||
      fieldIssue(document, matches[0])
    )
      return blocked(
        'This step’s source is not one valid Hearing reference. Review its shared value and choose a Hearing source for the whole domain group in Staff page before adding notice work.',
      );
    return resolvedReference(matches[0], 'step');
  }

  if (document.subjectTypeCode === 'hearing_cycle')
    return {
      kind: 'resolved',
      source: { bindingMode: 'linked-subject' },
      label: 'This workflow’s hearing',
      origin: 'workflow',
    };

  if (pendingField) {
    const issue = noticeHearingFieldIssue(document, pendingField);
    if (issue) return blocked(issue);
  }
  const fields = [
    ...(document.processData?.fields ?? []),
    ...(pendingField ? [pendingField] : []),
  ];
  const hearings = fields.filter(isHearingField);
  if (
    hearings.some(
      (field) =>
        fields.filter((item) => item.key === field.key).length !== 1 ||
        fieldIssue(document, field),
    )
  )
    return blocked(
      'A Hearing reference has an invalid or duplicate shared-value key or label. Review Data passed between steps before adding notice work.',
    );
  if (!hearings.length) return { kind: 'missing' };

  const selected = hearings.find((field) => field.key === selectedField);
  if (selectedField && !selected)
    return {
      kind: 'choice',
      options: hearings.map(({ key, label }) => ({ key, label })),
      issue:
        'The selected Hearing reference is no longer available. Choose a current reference.',
    };
  if (hearings.length === 1) return resolvedReference(hearings[0], 'reference');
  if (selected) return resolvedReference(selected, 'reference');
  return {
    kind: 'choice',
    options: hearings.map(({ key, label }) => ({ key, label })),
  };
}

/** A staged field is committed together with the notice requirement, never here. */
export function createNoticeHearingField(
  document: WorkflowDefinitionDocument,
  label = 'Hearing',
  requiredAtStart = true,
): WorkflowProcessDataField {
  const usedKeys = new Set(
    (document.processData?.fields ?? []).map((field) => field.key),
  );
  let key = 'hearing';
  for (let suffix = 2; usedKeys.has(key); suffix++) key = `hearing-${suffix}`;
  const field: WorkflowProcessDataField = {
    key,
    label: label.trim(),
    type: 'record-reference',
    recordTypeCode: 'scheduled-proceeding',
    requiredAtStart,
  };
  const issue = noticeHearingFieldIssue(document, field);
  if (issue) throw new Error(issue);
  return field;
}

export function noticeHearingFieldIssue(
  document: WorkflowDefinitionDocument,
  field: WorkflowProcessDataField,
): string | null {
  if (!isHearingField(field))
    return 'Choose a Hearing record reference for notice work.';
  return (
    processDataErrors(
      [...(document.processData?.fields ?? []), field],
      document,
    ).join(' ') || null
  );
}

function isHearingField(field: WorkflowProcessDataField): boolean {
  return (
    field.type === 'record-reference' &&
    field.recordTypeCode === 'scheduled-proceeding'
  );
}

function fieldIssue(
  document: WorkflowDefinitionDocument,
  field: WorkflowProcessDataField,
): string | null {
  return (
    processDataErrors([field], { ...document, nodes: [] }).join(' ') || null
  );
}

function resolvedReference(
  field: WorkflowProcessDataField,
  origin: 'step' | 'reference',
): NoticeSourceResolution {
  return {
    kind: 'resolved',
    source: { bindingMode: 'process-reference', referenceField: field.key },
    label: field.label,
    origin,
  };
}

function blocked(message: string): NoticeSourceResolution {
  return { kind: 'blocked', message };
}
